import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { useSiteInfo } from '../context/SiteContext';
import Sidebar from '../components/Sidebar';
import AdBlock from '../components/AdBlock';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Button } from '../components/ui/Button';
import { isValidContactPhone } from '../lib/utils';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: 'Tư vấn chung',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { siteInfo } = useSiteInfo();

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim()) {
      alert('Vui lòng nhập tên và số điện thoại!');
      return;
    }
    if (!isValidContactPhone(formData.phone)) {
      alert('Vui lòng kiểm tra lại số điện thoại.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'contacts'), {
        ...formData,
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        status: 'new',
        createdAt: new Date().toISOString()
      });
      
      // Prepare message for Zalo
      const zaloMessage = `Chào ${siteInfo.name},\nTôi là: ${formData.name}\nSĐT: ${formData.phone}\nQuan tâm dịch vụ: ${formData.service}\nGhi chú: ${formData.message}`;
      
      // Copy to clipboard
      try {
        await navigator.clipboard.writeText(zaloMessage);
        alert('Đã copy thông tin yêu cầu. Vui lòng dán (Paste) vào khung chat Zalo để gửi cho chúng tôi nhé!');
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }

      // Open Zalo
      const zaloUrl = `https://zalo.me/${siteInfo.phone.replace(/\./g, '').replace(/ /g, '')}`; 
      window.open(zaloUrl, '_blank');
      
      setFormData({ name: '', phone: '', service: 'Tư vấn chung', message: '' });
    } catch (error) {
      console.error('Error submitting contact:', error);
      alert('Có lỗi xảy ra, vui lòng thử lại sau.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 flex flex-col md:flex-row gap-8">
      <Helmet>
        <title>Liên hệ - {siteInfo.name}</title>
        <meta name="description" content={`Liên hệ với ${siteInfo.name} để nhận tư vấn và báo giá thi công khoan cọc nhồi, ép cừ, chống lún nhà.`} />
        <link rel="canonical" href={`${siteInfo.baseUrl || "https://khoancocnhoivietnam.vn"}/lien-he`} />
        <meta property="og:title" content={`Liên hệ - ${siteInfo.name}`} />
        <meta property="og:description" content={`Liên hệ với ${siteInfo.name} để nhận tư vấn và báo giá thi công khoan cọc nhồi, ép cừ, chống lún nhà.`} />
        <meta property="og:image" content="https://raw.githubusercontent.com/dinhkhanhtung/khoancocnhoi-website/main/Anh_khoan_coc_nhoi/z6586686864376_5d6f4365575fca0b349b19cf7fd3940c.jpg" />
        <meta property="og:url" content={`${siteInfo.baseUrl || "https://khoancocnhoivietnam.vn"}/lien-he`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="w-full md:w-3/4 min-w-0">
        <nav className="text-sm text-gray-500 mb-6 flex flex-wrap items-center gap-x-2 gap-y-1">
          <a href="/" className="hover:text-primary whitespace-nowrap">Trang chủ</a>
          <span>/</span>
          <span className="text-primary font-medium break-words max-w-full">Liên hệ</span>
        </nav>

        <div className="bg-white p-6 md:p-8 rounded-lg shadow-sm border">
          <h1 className="text-2xl md:text-3xl font-bold text-primary mb-8 pb-2 border-b-2 border-primary inline-block">
            Liên hệ với chúng tôi
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-primary mb-4 uppercase">
                {siteInfo.name}
              </h3>
              
              <div className="flex items-start space-x-4">
                <div className="bg-slate-100 p-3 rounded-full text-primary flex-shrink-0">
                  <MapPin size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-primary">Địa chỉ trụ sở</h4>
                  <p className="text-primary mt-1">
                    <a href="https://maps.app.goo.gl/r4SmC4ZYtN9uR3kr5" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                      {siteInfo.address}
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-slate-100 p-3 rounded-full text-primary flex-shrink-0">
                  <Phone size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-primary">Điện thoại</h4>
                  <p className="text-primary mt-1">{siteInfo.phone}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-slate-100 p-3 rounded-full text-primary flex-shrink-0">
                  <Mail size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-primary">Email</h4>
                  <p className="text-primary mt-1">{siteInfo.email}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-slate-100 p-3 rounded-full text-primary flex-shrink-0">
                  <Clock size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-primary">Giờ làm việc</h4>
                  <p className="text-primary mt-1">Thứ 2 - Thứ 7: 7:30 - 17:30<br/>Chủ nhật: Nghỉ</p>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="flex flex-col space-y-4">
              <div className="h-[400px] bg-gray-200 rounded-lg overflow-hidden shadow-inner">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3723.473663215204!2d105.8054453154025!3d21.053730992301!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3135ab1966555555%3A0x5555555555555555!2zTmfDtSA3NyBYdcOibiBMYSwgVMOieSBI4buTLCBIw6AgTuG7mWksIFZp4buHdCBOYW0!5e0!3m2!1svi!2s!4v1711333333333!5m2!1svi!2s"
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen={true} 
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Bản đồ đường đi"
                ></iframe>
              </div>
              <Button 
                variant="primary"
                size="lg"
                asChild
              >
                <a 
                  href="https://maps.app.goo.gl/r4SmC4ZYtN9uR3kr5" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <MapPin size={20} className="mr-2" />
                  Chỉ đường trên Google Maps
                </a>
              </Button>
            </div>
          </div>

          {/* Contact Form */}
          <div className="mt-12 bg-slate-50 p-6 md:p-8 rounded-xl border border-slate-100">
            <h2 className="text-2xl font-bold text-primary uppercase mb-2 text-center">
              Nhận Báo Giá Nhanh
            </h2>
            <p className="text-center text-primary mb-6">Để lại thông tin, chúng tôi sẽ liên hệ tư vấn và báo giá ngay qua Zalo</p>
            
            <form onSubmit={handleContactSubmit} className="space-y-4 max-w-2xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Họ và tên *</label>
                  <input 
                    type="text" 
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Nhập họ tên"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Số điện thoại (Zalo) *</label>
                  <input 
                    type="tel" 
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Nhập số điện thoại"
                    value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dịch vụ quan tâm</label>
                <select 
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                  value={formData.service}
                  onChange={e => setFormData({...formData, service: e.target.value})}
                >
                  <option value="Tư vấn chung">Tư vấn chung</option>
                  <option value="Khoan cọc nhồi">Khoan cọc nhồi</option>
                  <option value="Ép cừ U200">Ép cừ U200</option>
                  <option value="Chống lún nhà">Chống lún nhà</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ghi chú thêm</label>
                <textarea 
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                  rows={3}
                  placeholder="Nhập yêu cầu chi tiết (nếu có)"
                  value={formData.message}
                  onChange={e => setFormData({...formData, message: e.target.value})}
                ></textarea>
              </div>
              <div className="text-center pt-2">
                <Button 
                  type="submit" 
                  variant="primary"
                  size="lg"
                  loading={isSubmitting}
                  icon={<Send size={18} />}
                >
                  {isSubmitting ? 'Đang gửi...' : 'GỬI YÊU CẦU & MỞ ZALO'}
                </Button>
              </div>
            </form>
          </div>
        </div>
        
        {/* Banner QC */}
        <AdBlock position="contact_bottom" className="mt-8" />
      </div>
      
      <div className="w-full md:w-1/4 md:shrink-0">
        <Sidebar />
      </div>
    </div>
  );
}

